import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ExamPageShell from '../components/layout/ExamPageShell';
import ExamBadge from '../components/common/ExamBadge';
import { getExamDashboard, listExamProjects } from '../api/examApi';

function StatCard({ label, value, sub, color }) {
  return (
    <div className="exam-stat-card">
      <div className="exam-stat-label">{label}</div>
      <div className="exam-stat-value" style={color ? { color } : {}}>
        {value ?? <span className="exam-spinner" style={{ width: 22, height: 22 }} />}
      </div>
      {sub && <div className="exam-stat-change">{sub}</div>}
    </div>
  );
}

function QuickActionCard({ icon, title, desc, to, onClick }) {
  const inner = (
    <div className="exam-card" style={{
      display: 'flex', gap: 14, alignItems: 'flex-start',
      cursor: 'pointer', transition: 'box-shadow var(--exam-transition)',
      textDecoration: 'none', color: 'inherit',
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--exam-shadow-md)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'var(--exam-shadow-xs)'}
    >
      <div className="exam-stat-icon" style={{ background: 'var(--exam-primary-light)', color: 'var(--exam-primary)', flexShrink: 0 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--exam-text)' }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--exam-text-muted)', marginTop: 2 }}>{desc}</div>
      </div>
    </div>
  );
  if (to) return <Link to={to} style={{ textDecoration: 'none' }}>{inner}</Link>;
  return <div onClick={onClick}>{inner}</div>;
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export default function ExamDashboard() {
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getExamDashboard().catch(() => null),
      listExamProjects().catch(() => []),
    ]).then(([s, p]) => {
      setStats(s);
      setProjects((p || []).slice(0, 5));
    }).finally(() => setLoading(false));
  }, []);

  return (
    <ExamPageShell
      breadcrumbs={[{ label: 'Dashboard' }]}
      headerActions={
        <Link to="/examination/projects" className="exam-btn exam-btn-primary exam-btn-md">
          + New Project
        </Link>
      }
    >
      <div className="exam-page-header">
        <div>
          <h1 className="exam-page-title">Dashboard</h1>
          <p className="exam-page-subtitle">Overview of your examination workspace</p>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="exam-stat-grid">
        <StatCard
          label="Active Projects"
          value={stats?.active_projects ?? (loading ? undefined : 0)}
          sub="currently open"
        />
        <StatCard
          label="Questions Generated"
          value={stats?.total_questions ?? (loading ? undefined : 0)}
          sub={stats ? `${stats.approved_questions} approved` : ''}
          color={stats?.approved_questions ? 'var(--exam-success)' : undefined}
        />
        <StatCard
          label="Pending Evaluations"
          value={stats?.pending_evaluations ?? (loading ? undefined : 0)}
          sub="need faculty review"
          color={stats?.pending_evaluations > 0 ? 'var(--exam-warning)' : undefined}
        />
        <StatCard
          label="Completed Evaluations"
          value={stats?.completed_evaluations ?? (loading ? undefined : 0)}
          sub="this semester"
          color="var(--exam-success)"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>
        {/* Recent Projects */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Recent Projects</h2>
            <Link to="/examination/projects" style={{ fontSize: 12.5, color: 'var(--exam-primary)', textDecoration: 'none', fontWeight: 500 }}>
              View all →
            </Link>
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <div className="exam-spinner" />
            </div>
          ) : projects.length === 0 ? (
            <div className="exam-card" style={{ textAlign: 'center', padding: '40px 24px' }}>
              <p style={{ color: 'var(--exam-text-muted)', margin: 0, fontSize: 13 }}>
                No projects yet. Create your first examination project to get started.
              </p>
              <Link to="/examination/projects" className="exam-btn exam-btn-primary exam-btn-md" style={{ marginTop: 14, display: 'inline-flex' }}>
                + New Project
              </Link>
            </div>
          ) : (
            <div className="exam-table-wrap">
              <table className="exam-table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Course</th>
                    <th>Status</th>
                    <th>Updated</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 500, maxWidth: 220 }}>
                        <Link to={`/examination/projects/${p.id}`} style={{ color: 'var(--exam-text)', textDecoration: 'none' }}>
                          {p.title}
                        </Link>
                      </td>
                      <td style={{ color: 'var(--exam-text-secondary)' }}>
                        {p.course_code || p.course_name || '—'}
                      </td>
                      <td><ExamBadge status={p.status} label={p.status} dot /></td>
                      <td style={{ color: 'var(--exam-text-muted)' }}>{formatDate(p.updated_at)}</td>
                      <td>
                        <Link to={`/examination/projects/${p.id}`} className="exam-btn exam-btn-ghost exam-btn-sm">
                          Open →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div>
          <h2 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600 }}>Quick Actions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <QuickActionCard
              icon={
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" width="20" height="20">
                  <circle cx="10" cy="10" r="8"/><line x1="10" y1="6" x2="10" y2="14"/><line x1="6" y1="10" x2="14" y2="10"/>
                </svg>
              }
              title="New Project"
              desc="Start a new examination project"
              to="/examination/projects"
            />
            <QuickActionCard
              icon={
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" width="20" height="20">
                  <rect x="3" y="3" width="14" height="14" rx="2"/>
                  <line x1="7" y1="10" x2="13" y2="10"/>
                  <line x1="10" y1="7" x2="10" y2="13"/>
                </svg>
              }
              title="Upload Curriculum"
              desc="Add source material to a project"
              to="/examination/projects"
            />
            <QuickActionCard
              icon={
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" width="20" height="20">
                  <path d="M4 4h12v8H4z"/><path d="M8 16h4M10 12v4"/>
                </svg>
              }
              title="Generate Question Paper"
              desc="Build & export a question set"
              to="/examination/projects"
            />
            <QuickActionCard
              icon={
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" width="20" height="20">
                  <polyline points="3,17 7,12 11,15 17,7"/>
                  <line x1="3" y1="17" x2="17" y2="17"/>
                </svg>
              }
              title="View Analytics"
              desc="CO/PO attainment & insights"
              to="/examination/analytics"
            />
          </div>
        </div>
      </div>
    </ExamPageShell>
  );
}
